import {
  isWorkbookValueListOperator,
  type WorkbookDistinctValues,
  type WorkbookColumnarSheet,
} from '@erp/shared-types';
import type { ShownDistinctValues } from './sampleDistinctValues';

type Filter = WorkbookColumnarSheet['filters'][number];

/** A Field with at most this many Distinct values lists them all as checkboxes. */
export const VALUE_LIST_CHECKBOXES = 100;
/** The value that stands for the (Empty) entry in a Value list filter. */
export const EMPTY_ENTRY = '';

/** One checkbox of a Field's value list. */
export interface ValueListEntry {
  value: string;
  /** Rows holding the value before filters, when the Result's Distinct values know it. */
  count?: number;
  checked: boolean;
}

/**
 * A column's value list, as its filter menu shows it: every value when the
 * Field has few enough (`complete`), else the values its filters name and
 * those picked by search. `others` is whether values the list leaves out are
 * kept; `errors` whether Error cells are, null when there are none to show.
 */
export interface ValueList {
  entries: ValueListEntry[];
  complete: boolean;
  others: boolean;
  errors: boolean | null;
}

/**
 * The filters a column's value list stands for: its Value list filter, the
 * empty filters it shows as (Empty) and the Error filters it shows as (Errors).
 */
function inValueList(filter: Filter, column: string): boolean {
  return (
    filter.column === column &&
    (isWorkbookValueListOperator(filter.operator) ||
      filter.operator === 'is-null' ||
      filter.operator === 'is-not-null' ||
      filter.operator === 'is-error' ||
      filter.operator === 'is-not-error')
  );
}

/** A value list filter's values as the checkbox list reads them: Is Empty is only (Empty). */
function namedValues(filter: Filter | undefined) {
  if (!filter) return null;
  if (filter.operator === 'is-null')
    return { keeps: true, values: [EMPTY_ENTRY] };
  if (filter.operator === 'is-not-null')
    return { keeps: false, values: [EMPTY_ENTRY] };
  return {
    keeps: filter.operator === 'is-one-of',
    values: filter.values ?? [],
  };
}

/**
 * Whether a Field's Distinct values are every value (`complete`) and whether
 * its list shows them (`listed`): when complete, or a sample small enough.
 */
export function distinctListing(distinct: ShownDistinctValues | undefined): {
  complete: boolean;
  listed: boolean;
} {
  if (!distinct || distinct.values.length > VALUE_LIST_CHECKBOXES)
    return { complete: false, listed: false };
  const complete = !distinct.truncated;
  return { complete, listed: complete || distinct.sampleRows !== undefined };
}

/**
 * A column's value list, from its filters and the Result's Distinct values;
 * `picked` adds values chosen by search. A sample's values (counted from a
 * preview's first rows) are listed too, but never make it complete: values
 * the sample didn't see stay kept.
 */
export function worksheetValueList(
  filters: readonly Filter[],
  column: string,
  distinct: ShownDistinctValues | undefined,
  picked: readonly string[] = [],
): ValueList {
  const own = filters.filter((filter) => inValueList(filter, column));
  const named = namedValues(
    own.find(
      (filter) =>
        filter.operator !== 'is-error' && filter.operator !== 'is-not-error',
    ),
  );
  const error = own.find(
    (filter) =>
      filter.operator === 'is-error' || filter.operator === 'is-not-error',
  )?.operator;
  const { complete, listed } = distinctListing(distinct);
  const counts = new Map(
    distinct?.values.map((entry) => [entry.value, entry.count]),
  );
  if (distinct?.emptyCount) counts.set(EMPTY_ENTRY, distinct.emptyCount);
  const values = [
    ...(listed ? (distinct?.values.map((entry) => entry.value) ?? []) : []),
    ...(named?.values ?? []),
    ...picked,
  ];
  const listsEmpty =
    values.includes(EMPTY_ENTRY) || (listed && !!distinct?.emptyCount);
  // (Empty) goes last, as in a spreadsheet's filter list.
  const ordered = [
    ...new Set(values.filter((value) => value !== EMPTY_ENTRY)),
    ...(listsEmpty ? [EMPTY_ENTRY] : []),
  ];
  const onlyErrors = error === 'is-error';
  const checked = (value: string) =>
    !onlyErrors && (!named || named.values.includes(value) === named.keeps);
  return {
    entries: ordered.map((value) => ({
      value,
      count: counts.get(value),
      checked: checked(value),
    })),
    complete,
    others: !onlyErrors && !named?.keeps,
    // A value list never keeps an Error cell.
    errors:
      distinct?.errorCount || error ? !named && error !== 'is-not-error' : null,
  };
}

/** Whether a value list keeps every value, none, or some. */
function valuesKept(list: ValueList): 'all' | 'none' | 'some' {
  const checked = list.entries.filter((entry) => entry.checked).length;
  const unchecked = list.entries.length - checked;
  if (!unchecked && (list.complete || list.others)) return 'all';
  if (!checked && (list.complete || !list.others)) return 'none';
  return 'some';
}

/** Whether a value list keeps every value, so picking one means keep only it. */
export function keepsAllValues(list: ValueList): boolean {
  return valuesKept(list) === 'all';
}

/**
 * Whether (Errors) can change: a value list that keeps some values never
 * keeps an Error cell, so (Errors) stays unchecked until it keeps all or none.
 */
export function errorsLocked(list: ValueList): boolean {
  return valuesKept(list) === 'some';
}

/** The filters a value list stands for: a Value list or empty filter, an Error filter, both or neither. */
export function valueListFilters(list: ValueList, column: string): Filter[] {
  const kept = valuesKept(list);
  if (kept === 'all')
    return list.errors === false
      ? [{ column, operator: 'is-not-error', value: '' }]
      : [];
  if (kept === 'none' && list.errors)
    return [{ column, operator: 'is-error', value: '' }];
  const checked = list.entries.filter((entry) => entry.checked);
  const unchecked = list.entries.filter((entry) => !entry.checked);
  // A complete list names whichever side is shorter; otherwise `others` says which.
  const excludes = list.complete
    ? unchecked.length <= checked.length
    : list.others;
  const values = (excludes ? unchecked : checked).map((entry) => entry.value);
  if (values.length === 1 && values[0] === EMPTY_ENTRY)
    return [
      { column, operator: excludes ? 'is-not-null' : 'is-null', value: '' },
    ];
  return [
    {
      column,
      operator: excludes ? 'is-not-one-of' : 'is-one-of',
      value: '',
      values,
    },
  ];
}

/**
 * Replaces a column's value list filters with `next` where the first of them
 * stood, so the other filters keep their order.
 */
export function replaceValueListFilters(
  filters: readonly Filter[],
  column: string,
  next: readonly Filter[],
): Filter[] {
  const at = filters.findIndex((filter) => inValueList(filter, column));
  const before = (at < 0 ? filters : filters.slice(0, at)).filter(
    (filter) => !inValueList(filter, column),
  );
  const after = (at < 0 ? [] : filters.slice(at)).filter(
    (filter) => !inValueList(filter, column),
  );
  return [...before, ...next, ...after];
}

/** A value list with one entry toggled. */
export function toggleValueListEntry(
  list: ValueList,
  value: string,
): ValueList {
  return {
    ...list,
    entries: list.entries.map((entry) =>
      entry.value === value ? { ...entry, checked: !entry.checked } : entry,
    ),
  };
}

/** A value list keeping every value, including those it leaves out, and (Errors), or none of them. */
export function selectAllValues(list: ValueList, checked: boolean): ValueList {
  return {
    ...list,
    others: checked,
    errors: list.errors === null ? null : checked,
    entries: list.entries.map((entry) => ({ ...entry, checked })),
  };
}

/**
 * Adds a value picked by search, checked. On a column that keeps every value
 * it means keep only the picked value.
 */
export function pickValue(list: ValueList, value: string): ValueList {
  const listed = list.entries.some((entry) => entry.value === value)
    ? list.entries
    : [...list.entries, { value, checked: true }];
  if (valuesKept(list) !== 'all')
    return {
      ...list,
      entries: listed.map((entry) =>
        entry.value === value ? { ...entry, checked: true } : entry,
      ),
    };
  return {
    ...list,
    others: false,
    errors: list.errors === null ? null : false,
    entries: listed.map((entry) => ({
      ...entry,
      checked: entry.value === value,
    })),
  };
}

/** How many search suggestions a Field with too many values to list offers. */
const SUGGESTIONS = 8;

/**
 * A value list as its filter menu shows it for a search: the entries that
 * match, and for a list that isn't complete the Distinct values to suggest
 * and whether the typed text can be added as a value of its own. `text` is
 * how a value shows, which the search matches too.
 */
export function searchValueList(
  list: ValueList,
  distinct: WorkbookDistinctValues | undefined,
  search: string,
  text: (value: string) => string,
) {
  const query = search.trim().toLowerCase();
  const matches = (value: string) =>
    !query ||
    value.toLowerCase().includes(query) ||
    text(value).toLowerCase().includes(query);
  const listed = new Set(list.entries.map((entry) => entry.value));
  const suggestions =
    query && !list.complete
      ? (distinct?.values ?? [])
          .filter((entry) => !listed.has(entry.value) && matches(entry.value))
          .slice(0, SUGGESTIONS)
      : [];
  const typed = search.trim();
  return {
    shown: list.entries.filter((entry) => matches(entry.value)),
    suggestions,
    typed,
    canAddTyped:
      !!typed &&
      !listed.has(typed) &&
      !suggestions.some((entry) => entry.value === typed),
    allChecked:
      list.entries.every((entry) => entry.checked) &&
      (list.complete || list.others) &&
      list.errors !== false,
  };
}

/** What a cell's context menu filters by: its value, Empty, or its error. */
export type CellFilter =
  | { kind: 'value'; value: string }
  | { kind: 'empty' }
  | { kind: 'errors' }
  | { kind: 'na' };

/**
 * A filter change from a Cell menu or value list, before a Result shows it:
 * rows whose `column` cell is `cell` go, or with `keep` only those stay.
 */
export interface PendingFilter {
  column: string;
  cell: CellFilter;
  keep: boolean;
}

/** Told of a filter change as it is made, with the Worksheet's filters after it. */
export type NotePendingFilter = (
  pending: PendingFilter,
  filters: readonly Filter[],
) => void;

/**
 * A column's filters after "Filter out" (`keep: false`) or "Filter to only"
 * (`keep: true`) a cell: out extends what the column removes, to only replaces
 * what it keeps. Values and Empty edit the value list; errors its (Errors).
 */
export function filterByCell(
  filters: readonly Filter[],
  column: string,
  cell: CellFilter,
  keep: boolean,
): Filter[] {
  if (cell.kind === 'na') {
    // Only an Error cell matches Is #N/A, so the value list filters go.
    return replaceValueListFilters(
      filters.filter(
        (filter) => filter.column !== column || filter.operator !== 'is-na',
      ),
      column,
      [{ column, operator: 'is-na', value: '' }],
    );
  }
  // The value list entry the cell is, or null for (Errors).
  const target =
    cell.kind === 'value'
      ? cell.value
      : cell.kind === 'empty'
        ? EMPTY_ENTRY
        : null;
  let list = worksheetValueList(
    filters,
    column,
    undefined,
    target === null ? [] : [target],
  );
  if (keep) {
    list = selectAllValues(list, false);
    list =
      target === null
        ? { ...list, errors: true }
        : pickValue({ ...list, errors: null }, target);
  } else
    list =
      target === null
        ? { ...list, errors: false }
        : {
            ...list,
            entries: list.entries.map((entry) =>
              entry.value === target ? { ...entry, checked: false } : entry,
            ),
          };
  return replaceValueListFilters(
    filters,
    column,
    valueListFilters(list, column),
  );
}
